import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  FileArchive,
  FileText,
  Globe,
  Image as ImageIcon,
  KeyRound,
  Link2,
  Loader2,
  Package,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AdminModal, Field, Pill, inputCls, textareaCls } from "@/pages/admin/ui";
import { formatPrice } from "@/lib/format";

const fmtSize = (b) =>
  !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

const TYPE_OPTIONS = [
  { value: "file", label: "Fichier", icon: FileArchive },
  { value: "link", label: "Lien", icon: Link2 },
  { value: "link_external", label: "Lien externe", icon: Globe, as: "link" },
  { value: "key", label: "Clé de licence", icon: KeyRound },
  { value: "text", label: "Texte / Instructions", icon: FileText },
  { value: "image", label: "Image", icon: ImageIcon },
  { value: "document", label: "Document", icon: FileText, as: "file" },
];

export const TYPE_LABEL = { file: "Fichier", image: "Image", link: "Lien", key: "Clé de licence", text: "Texte" };
export const TYPE_ICON = { file: FileArchive, image: ImageIcon, link: Link2, key: KeyRound, text: FileText };

const ACCESS_OPTIONS = [
  { value: "permanent", label: "Accès permanent" },
  { value: "24h", label: "24 heures" },
  { value: "7d", label: "7 jours" },
  { value: "30d", label: "30 jours" },
  { value: "90d", label: "90 jours" },
  { value: "custom", label: "Date personnalisée" },
];
const ACCESS_LABELS = Object.fromEntries(ACCESS_OPTIONS.map((o) => [o.value, o.label]));

const LIMIT_OPTIONS = [
  { value: 0, label: "Illimité" },
  { value: 1, label: "1 téléchargement" },
  { value: 2, label: "2 téléchargements" },
  { value: 3, label: "3 téléchargements" },
  { value: 5, label: "5 téléchargements" },
  { value: 10, label: "10 téléchargements" },
  { value: 20, label: "20 téléchargements" },
];
const limitLabel = (n) => (LIMIT_OPTIONS.find((o) => o.value === Number(n)) || LIMIT_OPTIONS[0]).label;

const STEPS = [
  { id: 1, title: "Sélection du produit", sub: "Choisissez le produit concerné" },
  { id: 2, title: "Contenus numériques", sub: "Ajoutez les fichiers, liens, clés…" },
  { id: 3, title: "Options et restrictions", sub: "Limites, durée d'accès, etc." },
  { id: 4, title: "Récapitulatif", sub: "Vérifiez et sauvegardez" },
];

const EMPTY_ITEM = {
  id: "", type: "link", title: "", url: "", value: "", content: "", description: "",
  file_path: "", file_name: "", file_size: 0, is_hidden: false, expires_at: "", download_limit: 0,
};

function Stepper({ step }) {
  return (
    <div className="flex flex-wrap items-center gap-y-2" data-testid="pd-stepper">
      {STEPS.map((s, i) => (
        <div key={s.id} className="flex items-center">
          <div className="flex items-center gap-2.5">
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-extrabold transition-colors duration-200 ${
                step > s.id ? "bg-emerald-500 text-white" : step === s.id ? "bg-brand text-white" : "border border-line bg-white text-slate-400"
              }`}
              data-testid={`pd-step-${s.id}`}
            >
              {step > s.id ? <CheckCircle2 className="h-4 w-4" /> : s.id}
            </span>
            <div className="hidden sm:block">
              <p className={`text-xs font-bold ${step >= s.id ? "text-ink" : "text-slate-400"}`}>{s.title}</p>
              <p className="text-[10px] text-slate-400">{s.sub}</p>
            </div>
          </div>
          {i < STEPS.length - 1 && <span className={`mx-3 hidden h-px w-8 md:block ${step > s.id ? "bg-brand" : "bg-line"}`} />}
        </div>
      ))}
    </div>
  );
}

function ItemModal({ open, item, deliveryId, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [typeChoice, setTypeChoice] = useState("link");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(item ? { ...EMPTY_ITEM, ...item } : EMPTY_ITEM);
      setTypeChoice(item ? item.type : "link");
    }
  }, [open, item]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const isFile = form.type === "file" || form.type === "image";

  const pickType = (opt) => {
    setTypeChoice(opt.value);
    setForm((f) => ({ ...f, type: opt.as || opt.value }));
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/admin/product-deliveries/${deliveryId}/upload`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, file_path: data.path, file_name: data.name, file_size: data.size, title: f.title || data.name }));
      toast.success("Fichier téléversé");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploading(false);
    }
  };

  const save = () => {
    if (!form.title.trim()) return toast.error("Nom requis");
    if (form.type === "link" && !form.url.trim()) return toast.error("URL requise");
    if (form.type === "key" && !form.value.trim()) return toast.error("Clé requise");
    if (form.type === "text" && !form.content.trim()) return toast.error("Texte requis");
    if (isFile && !form.file_path) return toast.error("Importez un fichier");
    onSave({ ...form, download_limit: Number(form.download_limit) || 0 });
  };

  return (
    <AdminModal open={open} onOpenChange={(v) => !v && onClose()} title={form.id ? "Modifier le contenu" : "Ajouter un contenu"}>
      <div className="space-y-4" data-testid="pd-item-modal">
        <Field label="Type de contenu">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                data-testid={`pd-item-type-${opt.value}`}
                onClick={() => pickType(opt)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold transition-colors duration-200 ${
                  typeChoice === opt.value ? "border-brand bg-brand-light text-brand" : "border-line text-slate-600 hover:border-brand/40"
                }`}
              >
                <opt.icon className="h-4 w-4 shrink-0" /> {opt.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label={form.type === "key" ? "Nom" : form.type === "link" ? "Nom du lien" : "Nom"}>
          <input data-testid="pd-item-title" value={form.title} onChange={set("title")} className={inputCls}
                 placeholder={form.type === "link" ? "Téléchargement principal" : "Nom du contenu"} />
        </Field>

        {form.type === "link" && (
          <Field label="URL">
            <input data-testid="pd-item-url" value={form.url} onChange={set("url")} className={inputCls} placeholder="https://exemple.com/telechargement" />
          </Field>
        )}
        {form.type === "key" && (
          <Field label="Clé">
            <input data-testid="pd-item-value" value={form.value} onChange={set("value")} className={`${inputCls} font-mono`} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" />
          </Field>
        )}
        {form.type === "text" && (
          <Field label="Texte / Instructions">
            <textarea data-testid="pd-item-content" value={form.content} onChange={set("content")} rows={5} className={textareaCls}
                      placeholder="Téléchargez le fichier principal, installez le logiciel puis utilisez la clé de licence fournie…" />
          </Field>
        )}
        {isFile && (
          <Field label={form.type === "image" ? "Image" : "Fichier"}>
            <input ref={fileRef} type="file" className="hidden" data-testid="pd-item-file-input"
                   accept={form.type === "image" ? "image/*" : undefined}
                   onChange={(e) => upload(e.target.files?.[0])} />
            {form.file_path ? (
              <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="pd-item-file-ready">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
                  {form.type === "image" ? <ImageIcon className="h-5 w-5 text-brand" /> : <FileArchive className="h-5 w-5 text-brand" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-ink">{form.file_name}</p>
                  <p className="text-[11px] text-slate-500">{fmtSize(form.file_size)}</p>
                </div>
                <Pill tone="green">Fichier prêt</Pill>
                <button type="button" onClick={() => fileRef.current?.click()}
                        className="text-xs font-bold text-brand hover:underline">Remplacer</button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="pd-item-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line px-4 py-6 text-xs font-bold text-slate-500 transition-colors duration-200 hover:border-brand hover:text-brand"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Téléversement…" : "Importer depuis mon ordinateur"}
              </button>
            )}
          </Field>
        )}

        <Field label="Description (facultatif)">
          <input value={form.description} onChange={set("description")} className={inputCls} placeholder="Courte description visible par le client" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Expiration (facultatif)" hint="Vide = durée d'accès par défaut">
            <input data-testid="pd-item-expires" type="date" value={form.expires_at} onChange={set("expires_at")} className={inputCls} />
          </Field>
          <Field label="Limite de téléchargements" hint="0 = limite par défaut">
            <input data-testid="pd-item-limit" type="number" min="0" value={form.download_limit} onChange={set("download_limit")} className={inputCls} />
          </Field>
        </div>

        <button
          type="button"
          data-testid="pd-item-toggle-active"
          onClick={() => setForm((f) => ({ ...f, is_hidden: !f.is_hidden }))}
          className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-xs font-bold transition-colors duration-200 ${
            form.is_hidden ? "border-line text-slate-500" : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          <span className="flex items-center gap-2">
            {form.is_hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {form.is_hidden ? "Contenu désactivé (masqué au client)" : "Contenu actif"}
          </span>
          <span className={`h-5 w-9 rounded-full p-0.5 transition-colors duration-200 ${form.is_hidden ? "bg-slate-300" : "bg-emerald-500"}`}>
            <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${form.is_hidden ? "" : "translate-x-4"}`} />
          </span>
        </button>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-ink">
            Annuler
          </button>
          <button type="button" data-testid="pd-item-save" onClick={save}
                  className="rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90">
            Enregistrer
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

export default function ProductDeliveryForm({ open, delivery, onClose, onSaved }) {
  const isEdit = Boolean(delivery?.id);
  const [step, setStep] = useState(1);
  const [doc, setDoc] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [defaultAccess, setDefaultAccess] = useState("permanent");
  const [defaultExpiresAt, setDefaultExpiresAt] = useState("");
  const [defaultLimit, setDefaultLimit] = useState(0);
  const [autoActivate, setAutoActivate] = useState(true);
  const [customMessage, setCustomMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [itemModal, setItemModal] = useState({ open: false, item: null });
  const debounce = useRef(null);

  const loadDetail = useCallback(async (id) => {
    const { data } = await api.get(`/admin/product-deliveries/${id}`);
    setDoc(data);
    setItems(data.items || []);
    setStatus(data.status || "pending");
    setDefaultAccess(data.default_access || "permanent");
    setDefaultExpiresAt(data.default_expires_at || "");
    setDefaultLimit(data.default_download_limit || 0);
    setAutoActivate(data.auto_activate !== false);
    setCustomMessage(data.custom_message || "");
  }, []);

  useEffect(() => {
    if (!open) return;
    setDoc(null);
    setItems([]);
    setStatus("pending");
    setDefaultAccess("permanent");
    setDefaultExpiresAt("");
    setDefaultLimit(0);
    setAutoActivate(true);
    setCustomMessage("");
    setSearch("");
    setResults([]);
    setStep(isEdit ? 2 : 1);
    if (delivery?.id) {
      loadDetail(delivery.id).catch((e) => toast.error(apiError(e, "Chargement impossible")));
    }
  }, [open, delivery, isEdit, loadDetail]);

  useEffect(() => {
    if (doc || !open || step !== 1) return;
    debounce.current && clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/products", { params: { q: search, limit: 20 } });
        setResults(data.items || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [search, doc, open, step]);

  const pickProduct = async (product) => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/product-deliveries", { product_id: product.id });
      await loadDetail(data.id);
      setStatus("active");
      setStep(2);
      toast.success("Produit sélectionné — ajoutez maintenant les contenus numériques");
    } catch (error) {
      toast.error(apiError(error, "Création impossible"));
    } finally {
      setCreating(false);
    }
  };

  const saveItem = (item) => {
    const withId = item.id ? item : { ...item, id: crypto.randomUUID() };
    setItems((list) => {
      const idx = list.findIndex((i) => i.id === withId.id);
      if (idx >= 0) {
        const next = [...list];
        next[idx] = withId;
        return next;
      }
      return [...list, withId];
    });
    setItemModal({ open: false, item: null });
  };

  const move = (index, dir) => {
    setItems((list) => {
      const next = [...list];
      const j = index + dir;
      if (j < 0 || j >= next.length) return list;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/product-deliveries/${doc.id}/content`, { items });
      await api.put(`/admin/product-deliveries/${doc.id}/settings`, {
        default_access: defaultAccess,
        default_expires_at: defaultAccess === "custom" ? defaultExpiresAt : "",
        default_download_limit: Number(defaultLimit),
        auto_activate: autoActivate,
        custom_message: customMessage,
      });
      await api.put(`/admin/product-deliveries/${doc.id}/status`, { status });
      toast.success("Produit numérique enregistré");
      onSaved();
      onClose();
    } catch (error) {
      toast.error(apiError(error, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const product = doc?.product;
  const counts = items.reduce((acc, i) => {
    if (!i.is_hidden) acc[i.type] = (acc[i.type] || 0) + 1;
    return acc;
  }, {});

  const ProductCardMini = () => (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-slate-50/60 p-3" data-testid="pd-product-card">
      {product?.image_url ? (
        <img src={product.image_url} alt="" className="h-12 w-12 rounded-lg border border-line object-cover" />
      ) : (
        <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
          <Package className="h-5 w-5 text-slate-300" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-extrabold text-ink">{product?.name}</p>
        <p className="text-xs text-slate-500">
          {formatPrice(product?.price || 0, "XOF")}{product?.category ? ` · ${product.category}` : ""}
        </p>
      </div>
      <Pill tone="blue">Produit numérique</Pill>
    </div>
  );

  return (
    <>
      <AdminModal open={open} onOpenChange={(v) => !v && onClose()} wide
                  title={isEdit ? "Modifier le produit numérique" : "Ajouter un produit numérique"}>
        <div className="space-y-5" data-testid="product-delivery-form">
          <Stepper step={step} />

          {step === 1 && (
            <div className="space-y-4" data-testid="pd-step-content-1">
              <Field label="Sélectionnez un produit" hint="La liste reprend les produits existants du catalogue — aucun doublon de produit n'est créé">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="pd-product-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un produit par nom, référence…"
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </Field>
              <div className="max-h-80 space-y-2 overflow-y-auto" data-testid="pd-product-results">
                {searching ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
                ) : results.length === 0 ? (
                  <p className="py-8 text-center text-xs text-slate-400">Aucun produit trouvé</p>
                ) : (
                  results.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`pd-product-result-${p.id}`}
                      onClick={() => pickProduct(p)}
                      disabled={creating}
                      className="flex w-full items-center gap-3 rounded-xl border border-line p-3 text-left transition-colors duration-200 hover:border-brand hover:bg-brand-light/40"
                    >
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="h-11 w-11 shrink-0 rounded-lg border border-line object-cover" />
                      ) : (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                          <Package className="h-5 w-5 text-slate-300" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-ink">{p.name}</span>
                        <span className="block text-xs text-slate-500">{formatPrice(p.price, "XOF")}</span>
                      </span>
                      <Plus className="h-4 w-4 shrink-0 text-brand" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="pd-step-content-2">
              <ProductCardMini />
              <div className="flex items-center justify-between">
                <p className="text-sm font-extrabold text-ink">Contenus numériques</p>
                <button
                  type="button"
                  data-testid="pd-item-add-btn"
                  onClick={() => setItemModal({ open: true, item: null })}
                  className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
                >
                  <Plus className="h-3.5 w-3.5" /> Ajouter un contenu
                </button>
              </div>
              {items.length === 0 ? (
                <p className="rounded-xl border border-dashed border-line py-8 text-center text-xs text-slate-400" data-testid="pd-items-empty">
                  Aucun contenu — ajoutez fichiers, liens, clés ou instructions.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-line" data-testid="pd-contents-table">
                  <table className="w-full min-w-[680px] text-left">
                    <thead>
                      <tr className="border-b border-line bg-slate-50/60 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        <th className="px-3 py-2.5">Type</th>
                        <th className="px-3 py-2.5">Nom du contenu</th>
                        <th className="px-3 py-2.5">Détails</th>
                        <th className="px-3 py-2.5">Téléchargements max.</th>
                        <th className="px-3 py-2.5">Durée d'accès</th>
                        <th className="px-3 py-2.5">Statut</th>
                        <th className="px-3 py-2.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const Icon = TYPE_ICON[item.type] || FileText;
                        return (
                          <tr key={item.id || index} className="border-b border-line/60 text-xs last:border-0" data-testid={`pd-item-row-${index}`}>
                            <td className="px-3 py-2.5">
                              <span className="flex items-center gap-1.5 font-semibold text-slate-600">
                                <Icon className="h-3.5 w-3.5 text-brand" /> {TYPE_LABEL[item.type]}
                              </span>
                            </td>
                            <td className="max-w-[140px] truncate px-3 py-2.5 font-bold text-ink">{item.title}</td>
                            <td className="max-w-[160px] truncate px-3 py-2.5 text-slate-500">
                              {item.file_name ? `${item.file_name}${item.file_size ? ` · ${fmtSize(item.file_size)}` : ""}` : item.url || (item.type === "key" ? item.value : item.type === "text" ? "Voir le contenu" : "—")}
                            </td>
                            <td className="px-3 py-2.5 text-slate-600">
                              {item.download_limit > 0 ? `${item.download_limit} téléchargement${item.download_limit > 1 ? "s" : ""}` : "Illimité"}
                            </td>
                            <td className="px-3 py-2.5 text-slate-600">
                              {item.expires_at ? `Expire le ${item.expires_at}` : "Accès permanent"}
                            </td>
                            <td className="px-3 py-2.5">{item.is_hidden ? <Pill tone="slate">Désactivé</Pill> : <Pill tone="green">Actif</Pill>}</td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-end gap-0.5">
                                <button type="button" onClick={() => move(index, -1)} data-testid={`pd-item-up-${index}`}
                                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-ink"><ArrowUp className="h-3.5 w-3.5" /></button>
                                <button type="button" onClick={() => move(index, 1)} data-testid={`pd-item-down-${index}`}
                                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-ink"><ArrowDown className="h-3.5 w-3.5" /></button>
                                <button type="button" onClick={() => setItemModal({ open: true, item })} data-testid={`pd-item-edit-${index}`}
                                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
                                <button type="button" onClick={() => setItems((l) => l.filter((_, i) => i !== index))} data-testid={`pd-item-delete-${index}`}
                                        className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {items.length > 0 && (
                <p className="text-[11px] text-slate-400" data-testid="pd-items-count">{items.length} contenu{items.length > 1 ? "s" : ""} ajouté{items.length > 1 ? "s" : ""}</p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="pd-step-content-3">
              <ProductCardMini />
              <div className="rounded-2xl border border-line p-4">
                <p className="text-sm font-extrabold text-ink">Paramètres d'accès global</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Définissez les règles générales d'accès pour tous les contenus de ce produit.
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Durée d'accès par défaut" hint="Appliquée si non spécifiée sur un contenu">
                    <select data-testid="pd-access-select" value={defaultAccess} onChange={(e) => setDefaultAccess(e.target.value)} className={inputCls}>
                      {ACCESS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Nombre de téléchargements par défaut" hint="Appliquée si non spécifiée sur un contenu">
                    <select data-testid="pd-limit-select" value={defaultLimit} onChange={(e) => setDefaultLimit(Number(e.target.value))} className={inputCls}>
                      {LIMIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                </div>
                {defaultAccess === "custom" && (
                  <Field label="Date d'expiration personnalisée" className="mt-4">
                    <input data-testid="pd-access-custom-date" type="date" value={defaultExpiresAt} onChange={(e) => setDefaultExpiresAt(e.target.value)} className={inputCls} />
                  </Field>
                )}
                <button
                  type="button"
                  data-testid="pd-auto-activate-toggle"
                  onClick={() => setAutoActivate((v) => !v)}
                  className={`mt-4 flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors duration-200 ${
                    autoActivate ? "border-brand/30 bg-brand-light/50" : "border-line"
                  }`}
                >
                  <span>
                    <span className={`block text-xs font-bold ${autoActivate ? "text-brand" : "text-slate-600"}`}>Accès après validation</span>
                    <span className="mt-0.5 block text-[11px] text-slate-500">
                      Les contenus seront accessibles automatiquement après validation de la commande.
                    </span>
                  </span>
                  <span className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors duration-200 ${autoActivate ? "bg-brand" : "bg-slate-300"}`}>
                    <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${autoActivate ? "translate-x-4" : ""}`} />
                  </span>
                </button>
                <Field label="Message personnalisé (optionnel)" hint="Ce message sera affiché au client dans sa page de téléchargements" className="mt-4">
                  <textarea data-testid="pd-custom-message" value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} rows={3} className={textareaCls}
                            placeholder="Merci pour votre achat ! Vos contenus numériques sont disponibles ci-dessous." />
                </Field>
                <Field label="Statut de la livraison" className="mt-4 sm:max-w-xs">
                  <select data-testid="pd-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                    <option value="active">Actif</option>
                    <option value="pending">En attente</option>
                    <option value="disabled">Désactivé</option>
                  </select>
                </Field>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="pd-step-content-4">
              <ProductCardMini />
              <div className="rounded-2xl border border-line p-4">
                <p className="text-sm font-extrabold text-ink">Récapitulatif</p>
                <div className="mt-3 space-y-2 text-xs">
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Contenus</span>
                    <span className="text-right font-semibold text-ink" data-testid="pd-recap-contents">
                      {items.length === 0 ? "Aucun" : [
                        counts.file ? `${counts.file} fichier${counts.file > 1 ? "s" : ""}` : "",
                        counts.image ? `${counts.image} image${counts.image > 1 ? "s" : ""}` : "",
                        counts.link ? `${counts.link} lien${counts.link > 1 ? "s" : ""}` : "",
                        counts.key ? `${counts.key} clé${counts.key > 1 ? "s" : ""}` : "",
                        counts.text ? `${counts.text} texte${counts.text > 1 ? "s" : ""}` : "",
                      ].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Durée d'accès par défaut</span>
                    <span className="font-semibold text-ink" data-testid="pd-recap-access">
                      {ACCESS_LABELS[defaultAccess]}{defaultAccess === "custom" && defaultExpiresAt ? ` (${defaultExpiresAt})` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Téléchargements par défaut</span>
                    <span className="font-semibold text-ink" data-testid="pd-recap-limit">{limitLabel(defaultLimit)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Accès après validation</span>
                    <span className="font-semibold text-ink">{autoActivate ? "Automatique" : "Manuel (par commande)"}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Statut</span>
                    <StatusRecap status={status} />
                  </div>
                  {customMessage.trim() && (
                    <div className="rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-600">
                      <span className="font-bold text-ink">Message client : </span>{customMessage}
                    </div>
                  )}
                </div>
              </div>
              <p className="flex items-start gap-2 rounded-xl border border-brand/15 bg-brand-light/40 p-3 text-[11px] leading-relaxed text-slate-600">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                Après qu'un client a passé commande et effectué son paiement, vous devrez valider manuellement la commande depuis son détail. Une fois validée, tous les contenus configurés ici seront automatiquement accessibles au client dans son espace.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
            {step > 1 ? (
              <button type="button" data-testid="pd-back-btn" onClick={() => setStep(step - 1)}
                      className="flex items-center gap-1.5 rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-ink">
                <ChevronLeft className="h-4 w-4" /> Retour
              </button>
            ) : (
              <button type="button" onClick={onClose}
                      className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-ink">
                Annuler
              </button>
            )}
            {step < 4 ? (
              <button
                type="button"
                data-testid="pd-continue-btn"
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !doc}
                className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90 disabled:opacity-50"
              >
                Continuer <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                data-testid="pd-save-btn"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer le produit numérique
              </button>
            )}
          </div>
        </div>
      </AdminModal>

      <ItemModal
        open={itemModal.open}
        item={itemModal.item}
        deliveryId={doc?.id || ""}
        onClose={() => setItemModal({ open: false, item: null })}
        onSave={saveItem}
      />
    </>
  );
}

function StatusRecap({ status }) {
  if (status === "active") return <Pill tone="green">Actif</Pill>;
  if (status === "disabled") return <Pill tone="rose">Désactivé</Pill>;
  return <Pill tone="amber">En attente</Pill>;
}
